import React, { FC, useContext, useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import useIsBreakpoint from '../../../hooks/use-is-breakpoint/use-is-breakpoint';
import { BasketAddProductErrorLabelsType } from '../../../models/basket-add-product-error-labels-type';
import { BasketClearQuoteLabelsType } from '../../../models/basket-clear-quote-labels-type';
import BreakpointEnum from '../../../models/breakpoint-enum';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import { SpecialPricingType } from '../../../models/special-pricing-type';
import { YouRecentlyViewedType } from '../../../models/you-recently-viewed-type';
import pricingRequest from '../../../services/customer-api/endpoints/pricing/request';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import Carousel from '../../molecules/carousel/carousel';
import ProductCard from '../../molecules/product-card/product-card';
import { BasketStateContext } from '../../providers/basket/basket.provider';
import useGetRecentBasketProducts from '../../providers/basket/hooks/use-get-recent-basket-items';
import useIsLoggedIn from '../../providers/user/hooks/use-is-logged-in';

const YouRecentlyViewed: FC<YouRecentlyViewedType> = ({ heading, labels }) => {
  const addProductErrorLabels: BasketAddProductErrorLabelsType = {
    addProductErrorHeading: labels.addProductErrorHeading,
    addProductErrorDescription: labels.addProductErrorDescription,
    addProductErrorBody: labels.addProductErrorBody,
    insufficientClearanceStockError: labels.insufficientClearanceStockError,
  };

  const clearQuoteLabels: BasketClearQuoteLabelsType = {
    clearQuoteAccept: labels.clearQuoteAccept,
    clearQuoteCancel: labels.clearQuoteCancel,
    clearQuoteDescription: labels.clearQuoteDescription,
    clearQuoteHeading: labels.clearQuoteHeading,
    clearQuoteHelp: labels.clearQuoteHelp,
  };

  const [basketItems, setBasketItems] = useState<ProductLineItemType[]>();
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [specialPrices, setSpecialPrices] = useState<SpecialPricingType[]>();
  const isLoggedIn = useIsLoggedIn();
  const basketState = useContext(BasketStateContext);
  const recentBasketProducts = useGetRecentBasketProducts();
  const breakpoint = useIsBreakpoint();
  const breakpoints = {
    0: {
      centeredSlides: true,
      slidesPerGroup: 1,
      slidesPerView: 1.2,
    },
    641: {
      centeredSlides: false,
      slidesPerGroup: 2,
      slidesPerView: 2,
    },
    1025: {
      slidesPerGroup: 3,
      slidesPerView: 3,
    },
  };

  useEffect(() => {
    if (!basketState?.recentId) {
      setIsFetched(true);

      return;
    }

    recentBasketProducts().then((response: ProductLineItemType[]) => {
      setBasketItems(response);
      setIsFetched(true);
    });
  }, [basketState?.recentId]);

  useEffect(() => {
    if (!isLoggedIn || !basketState?.itemCount) {
      return;
    }

    pricingRequest(basketItems).then((response: SpecialPricingType[]) =>
      setSpecialPrices(response)
    );
  }, [isLoggedIn, basketState?.itemCount]);

  if (!isFetched) {
    return <LoadingSpinner className="h-96" />;
  }

  if (isFetched && (basketItems?.length || 0) < 3) {
    return null;
  }

  return (
    <div className="border-t border-grey-100 py-component-spacing-mobile lg:py-component-spacing">
      <Carousel
        breakpoints={breakpoints}
        heading={heading}
        numberOfSlides={basketItems.length}
        uniqueId="recently-viewed-products"
      >
        {basketItems.map((basketItem: ProductLineItemType, index: number) => (
          <SwiperSlide key={basketItem.product.guid}>
            {({ isActive, isVisible }) => (
              <ProductCard
                addProductErrorLabels={addProductErrorLabels}
                animate
                basketItem={basketItem}
                clearQuoteLabels={clearQuoteLabels}
                featured={basketItem.product.properties.featuredProduct === '1'}
                isActive={
                  breakpoint === BreakpointEnum.sm ? isActive : isVisible
                }
                isRecentlyViewed
                labels={labels}
                specialPrice={
                  isLoggedIn && specialPrices ? specialPrices[index] : null
                }
                hideStockAmount
              />
            )}
          </SwiperSlide>
        ))}
      </Carousel>
    </div>
  );
};

export default YouRecentlyViewed;
